import React, { useState } from 'react';
import ProgressBar from './ProgressBar';
import useArticleSync from './useArticleSync';
import { buttonStyle, cardDescriptionStyle, cardStyle, cardTitleStyle, fieldsStyle, fieldStyle, inputStyle, labelStyle } from './styles';

const PHASE_LABELS = {
    listing: 'Hämtar aktiva artiklar från Fortnox',
    importing: 'Synkar artiklar',
    listingStale: 'Letar efter artiklar som inte synkats på länge',
    stale: 'Synkar artiklar som inte uppdaterats på länge',
};

const MAX_LISTED_ERRORS = 50;

const resultStyle = (ok) => ({
    margin: '0.75rem 0 0',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.25rem',
    color: ok ? '#1b6e2d' : '#b3261e',
    backgroundColor: ok ? '#e3f4e7' : '#fde7e5',
});

const Summary = ({ status, imported, skipped, checked, errors, error }) => {
    if (status === 'idle' || status === 'running') return null;

    const failed = status === 'failed';
    const text = `${imported} artiklar synkade, ${skipped} hoppades över (redan synkade), ${checked} äldre artiklar synkade, ${errors.length} fel`;
    const heading = { succeeded: 'Klart', cancelled: 'Avbrutet', failed: `Fel: ${error}` }[status];

    return (
        <div role="status" style={resultStyle(status === 'succeeded' && errors.length === 0)}>
            <div>{failed ? heading : `${heading}: ${text}`}</div>
            {errors.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                    <summary>Visa fel</summary>
                    <ul style={{ margin: '0.5rem 0 0', fontSize: '0.8rem' }}>
                        {errors.slice(0, MAX_LISTED_ERRORS).map((e, index) => (
                            <li key={index}>{e.articleNumber}: {e.error}</li>
                        ))}
                    </ul>
                    {errors.length > MAX_LISTED_ERRORS && <div>…och {errors.length - MAX_LISTED_ERRORS} fler, se loggen.</div>}
                </details>
            )}
        </div>
    );
};

const ArticleSync = () => {
    const [daysBack, setDaysBack] = useState('');
    const [skipHours, setSkipHours] = useState('4');
    const sync = useArticleSync();
    const { running, phase, current, total } = sync;

    const toNumber = (value) => (value === '' ? null : Number(value));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!running) sync.run(toNumber(daysBack), toNumber(skipHours));
    };

    return (
        <form onSubmit={handleSubmit} style={cardStyle(false)}>
            <h3 style={cardTitleStyle}>Artikelsynk från Fortnox</h3>
            <p style={cardDescriptionStyle}>
                Hämtar aktiva webbartiklar från Fortnox och skapar eller uppdaterar dem i Litium med detaljer, en artikel i taget med förloppsvisning.
                Lämna antal dagar tomt för att synka alla artiklar, annars synkas bara artiklar ändrade de senaste dagarna.
                Därefter synkas även artiklar som inte uppdaterats på 3 dagar. Är de inaktiva eller borta i Fortnox avpubliceras de i Litium.
                Artiklar som redan synkats de senaste timmarna hoppas över utan att detaljer hämtas, ange 0 (eller lämna tomt) för att synka alla.
            </p>

            <div style={{ ...fieldsStyle, alignItems: 'flex-end' }}>
                <div style={fieldStyle(true)}>
                    <label htmlFor="article-sync-daysBack" style={labelStyle}>Antal dagar bakåt</label>
                    <input
                        id="article-sync-daysBack"
                        type="number"
                        min={1}
                        value={daysBack}
                        placeholder="Antal dagar"
                        disabled={running}
                        onChange={(e) => setDaysBack(e.target.value)}
                        style={inputStyle}
                    />
                </div>
                <div style={fieldStyle(true)}>
                    <label htmlFor="article-sync-skipHours" style={labelStyle}>Hoppa över synkade senaste (timmar)</label>
                    <input
                        id="article-sync-skipHours"
                        type="number"
                        min={0}
                        value={skipHours}
                        placeholder="0 = synka alla"
                        disabled={running}
                        onChange={(e) => setSkipHours(e.target.value)}
                        style={inputStyle}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button type="submit" style={{ ...buttonStyle(running), marginTop: 0 }} disabled={running}>
                    Starta artikelsynk från Fortnox
                </button>
                {running && (
                    <button type="button" onClick={sync.stop} style={{ ...buttonStyle(false), marginTop: 0, color: '#111', backgroundColor: '#e6e6e6' }}>
                        Avbryt
                    </button>
                )}
            </div>

            {running && <ProgressBar label={PHASE_LABELS[phase] ?? 'Pågår'} current={current} total={total} />}
            <Summary {...sync} />
        </form>
    );
};

export default ArticleSync;
